import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  ArrowLeft,
  ChevronRight,
  Eye,
  CalendarDays,
  ThumbsUp,
  ThumbsDown,
  FileText,
  BookOpen,
  Clock,
  List,
  PlayCircle,
  Pencil,
  Save,
  X,
} from 'lucide-react';
import { knowledgeApi, Article } from '@/api/endpoints';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import clsx from 'clsx';
import { useAuthStore } from '@/stores/authStore';
import { normalizeArticleText, structureArticleContent } from './articleSections';
import DemoPlayer from './DemoPlayer';
import { ClipPopupHost, injectClipMarkers, KbClip } from './KbClipsRuntime';
import { sanitizeArticleHtml } from './sanitizeArticleHtml';
import { canManageKnowledgeArticles } from './permissions';

// Кто может редактировать статьи прямо с help.recore.su: те же роли,
// что и в admin-app (admin / developer / manager). Обычный клиент таких
// возможностей не получает. Бэк дополнительно чекает право на /kb/articles
// PATCH; UI скрывает кнопки если роль не подходит.
const estimateReadingTime = (content?: string): number => {
  if (!content) return 1;
  const text = content.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

const scrollToSection = (id: string) => {
  const node = document.getElementById(id);
  if (node) {
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canEdit = canManageKnowledgeArticles(user);
  const [helpfulVote, setHelpfulVote] = useState<'up' | 'down' | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  // Inline-editor для админов: title/excerpt/content редактируются прямо
  // на странице, без перехода в админку. Сохранение → PATCH /kb/articles
  // и инвалидация кэша react-query.
  const [editMode, setEditMode] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftExcerpt, setDraftExcerpt] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const returnTo = typeof location.state?.from === 'string' ? location.state.from : '/knowledge';

  const {
    data: article,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['knowledge-article', slug],
    queryFn: () => knowledgeApi.getArticleBySlug(slug!),
    enabled: !!slug,
  });

  const helpfulMutation = useMutation({
    mutationFn: () => knowledgeApi.markHelpful(article!.id),
    onSuccess: (data) => {
      setHelpfulVote('up');
      queryClient.setQueryData(['knowledge-article', slug], (old: Article | undefined) => {
        if (!old) return old;
        return { ...old, helpfulCount: data.helpfulCount };
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      knowledgeApi.updateArticle(article!.id, {
        title: draftTitle,
        excerpt: draftExcerpt,
        content: draftContent,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['knowledge-article', slug], updated);
      queryClient.invalidateQueries({ queryKey: ['knowledge-articles'] });
      setEditMode(false);
    },
  });

  // При входе в edit-mode подтягиваем текущие значения статьи в draft —
  // чтобы пользователь начинал с того, что было, а не с пустого поля.
  useEffect(() => {
    if (editMode && article) {
      setDraftTitle(article.title || '');
      setDraftExcerpt(article.excerpt || '');
      setDraftContent(article.content || '');
    }
  }, [editMode, article]);

  const clips = useMemo<KbClip[]>(() => {
    const raw = (article as any)?.clips;
    return Array.isArray(raw) ? (raw as KbClip[]) : [];
  }, [article]);

  const structuredContent = useMemo(() => {
    const safeContent = article?.content ? sanitizeArticleHtml(article.content) : article?.content;
    const sc = structureArticleContent(safeContent);
    if (clips.length === 0) return sc;
    // Shared state so occurrence counting + "one-wrap-per-clip" both
    // apply across the ENTIRE article, not per-section. Without this,
    // the same term lights up once in every section.
    const state = {
      usedClipIdx: new Set<number>(),
      termSeen: new Map<string, number>(),
    };
    return {
      ...sc,
      introHtml: sc.introHtml ? injectClipMarkers(sc.introHtml, clips, state) : sc.introHtml,
      sections: sc.sections.map((s) => ({ ...s, html: injectClipMarkers(s.html, clips, state) })),
    };
  }, [article?.content, clips]);

  const showIntroPanel =
    !!article &&
    !!structuredContent.introHtml &&
    normalizeArticleText(structuredContent.introHtml) !== normalizeArticleText(article.excerpt);

  if (isLoading) {
    return <LoadingSpinner fullPage text="Загрузка статьи..." />;
  }

  if (error || !article) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <BookOpen className="mb-4 h-12 w-12 text-[#727b96]" />
        <h2 className="cc-display text-lg font-bold text-[#e7eaf3]">Статья не найдена</h2>
        <p className="mt-1 text-sm text-[#9aa3bd]">
          Запрашиваемая статья не существует или была удалена.
        </p>
        <Button
          variant="primary"
          className="mt-6"
          onClick={() => navigate(returnTo)}
          icon={<ArrowLeft className="h-4 w-4" />}
        >
          Вернуться в базу знаний
        </Button>
      </div>
    );
  }

  const readTime = estimateReadingTime(article.content);
  const overviewItems = [
    { label: 'Категория', value: article.category?.name || 'Без категории' },
    { label: 'Обновлена', value: format(new Date(article.updatedAt), 'dd MMMM yyyy', { locale: ru }) },
    { label: 'Время чтения', value: `${readTime} мин` },
    { label: 'Просмотры', value: `${article.viewCount} просмотров` },
  ];
  const relatedArticles = article.relatedArticles || [];
  const demoSteps = Array.isArray(article.demoSteps) ? article.demoSteps : [];
  const hasDemo = demoSteps.length > 0;

  return (
    <div className="animate-fade-in space-y-6 md:space-y-8">
      {clips.length > 0 && <ClipPopupHost clips={clips} />}
      <nav className="flex flex-wrap items-center gap-1 md:gap-2 text-xs md:text-sm text-zinc-500">
        <Link to={returnTo} className="hover:text-fg-primary transition-colors">
          База знаний
        </Link>
        <ChevronRight className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" />
        {article.category && (
          <>
            <Link
              to={`/knowledge?category=${article.category.id}`}
              className="hover:text-fg-primary transition-colors"
            >
              {article.category.name}
            </Link>
            <ChevronRight className="h-3 w-3 md:h-3.5 md:w-3.5 flex-shrink-0" />
          </>
        )}
        <span className="truncate font-medium text-fg-primary">{article.title}</span>
      </nav>

      <div className="grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-4 md:space-y-6">
          <Card padding="lg" className="kb-article-hero">
            <div className="kb-article-hero-head">
              <div className="kb-article-hero-badges">
                {article.category && <span className="kb-article-chip">{article.category.name}</span>}
                <span className="kb-article-chip">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {format(new Date(article.updatedAt), 'dd MMMM yyyy', { locale: ru })}
                </span>
                <span className="kb-article-chip">
                  <Clock className="h-3.5 w-3.5" />
                  {readTime} мин
                </span>
              </div>
              {editMode ? (
                <>
                  <input
                    type="text"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    className="cc-display w-full rounded-xl border border-line bg-fill-1 px-3 py-2 text-2xl md:text-4xl font-extrabold tracking-[-0.03em] text-fg-primary outline-none focus:border-accent-border"
                    placeholder="Заголовок статьи"
                  />
                  <textarea
                    value={draftExcerpt}
                    onChange={(e) => setDraftExcerpt(e.target.value)}
                    rows={2}
                    className="mt-3 w-full rounded-xl border border-line bg-fill-1 px-3 py-2 text-sm text-fg-secondary outline-none focus:border-accent-border"
                    placeholder="Краткое описание (excerpt)"
                  />
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <h1 className="cc-display text-2xl md:text-4xl font-extrabold tracking-[-0.03em] text-fg-primary">
                      {article.title}
                    </h1>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setEditMode(true)}
                        icon={<Pencil className="h-4 w-4" />}
                      >
                        Редактировать
                      </Button>
                    )}
                  </div>
                  {article.excerpt && <p className="kb-article-excerpt">{article.excerpt}</p>}
                </>
              )}
              {hasDemo && !editMode && (
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="mt-5 inline-flex items-center gap-2.5 rounded-xl border border-accent-border bg-accent-bg px-4 py-2.5 text-sm font-semibold text-accent transition-all hover:border-accent hover:opacity-90"
                >
                  <PlayCircle className="h-5 w-5" />
                  Показать наглядно
                  <span className="rounded-md bg-accent-bg px-1.5 py-0.5 text-xs font-medium text-accent">
                    {demoSteps.length} {demoSteps.length === 1 ? 'шаг' : 'шагов'}
                  </span>
                </button>
              )}
            </div>
          </Card>

          <Card padding="lg" className="kb-article-shell">
            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
                    HTML-контент статьи
                  </label>
                  <textarea
                    value={draftContent}
                    onChange={(e) => setDraftContent(e.target.value)}
                    rows={24}
                    className="w-full rounded-xl border border-line bg-fill-1 px-3 py-2.5 font-mono text-[13px] leading-relaxed text-fg-primary outline-none focus:border-accent-border"
                    placeholder="<h2>Заголовок раздела</h2><p>Текст…</p>"
                  />
                  <p className="mt-1.5 text-[11px] text-zinc-500">
                    Поддерживается HTML, выделение секций по тегам &lt;h2&gt;. Опасные теги (script, style, on*) удаляются на бэке.
                  </p>
                </div>
                {updateMutation.isError && (
                  <div className="rounded-xl border border-danger-border bg-danger-bg px-3.5 py-2.5 text-sm text-danger">
                    Не удалось сохранить: {(updateMutation.error as any)?.message || 'ошибка сервера'}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="primary"
                    onClick={() => updateMutation.mutate()}
                    loading={updateMutation.isPending}
                    icon={<Save className="h-4 w-4" />}
                  >
                    Сохранить
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setEditMode(false)}
                    icon={<X className="h-4 w-4" />}
                    disabled={updateMutation.isPending}
                  >
                    Отмена
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {showIntroPanel && (
                  <div className="kb-article-shell-intro">
                    <div className="kb-article-shell-intro-label">Введение</div>
                    <div
                      className="kb-article-content kb-article-shell-intro-body"
                      dangerouslySetInnerHTML={{ __html: structuredContent.introHtml }}
                    />
                  </div>
                )}

                <div className="kb-article-sections">
                  {structuredContent.sections.map((section, index) => (
                    <section key={section.id} id={section.id} className="kb-article-section">
                      <div className="kb-article-section-aside">
                        <div className="kb-article-section-number">
                          {String(index + 1).padStart(2, '0')}
                        </div>
                        <div className="kb-article-section-line" />
                      </div>
                      <div className="kb-article-section-main">
                        <div className="kb-article-section-heading">
                          <h2 className="kb-article-section-title">{section.title}</h2>
                        </div>
                        <div
                          className="kb-article-content kb-article-section-body"
                          dangerouslySetInnerHTML={{ __html: section.html }}
                        />
                      </div>
                    </section>
                  ))}
                </div>
              </>
            )}

            <div className="kb-article-feedback">
              <div>
                <div className="kb-article-panel-label">Оценка</div>
                <p className="mt-2 text-sm font-semibold text-[#e7eaf3]">
                  Эта статья помогла разобраться в вопросе?
                </p>
                <p className="mt-1 text-xs text-[#727b96]">
                  {article.helpfulCount} пользователей отметили материал как полезный.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={helpfulVote === 'up' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => {
                    if (!helpfulVote) helpfulMutation.mutate();
                  }}
                  disabled={!!helpfulVote}
                  loading={helpfulMutation.isPending}
                  icon={
                    <ThumbsUp
                      className={clsx('h-4 w-4', helpfulVote === 'up' && 'fill-current')}
                    />
                  }
                >
                  Да
                </Button>
                <Button
                  variant={helpfulVote === 'down' ? 'danger' : 'secondary'}
                  size="sm"
                  onClick={() => {
                    if (!helpfulVote) setHelpfulVote('down');
                  }}
                  disabled={!!helpfulVote}
                  icon={
                    <ThumbsDown
                      className={clsx('h-4 w-4', helpfulVote === 'down' && 'fill-current')}
                    />
                  }
                >
                  Нет
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-4 md:space-y-6 lg:sticky lg:top-24 self-start">
          {structuredContent.sections.length > 0 && (
            <Card padding="lg" className="kb-side-panel">
              <div className="kb-side-panel-title">Оглавление</div>
              <div className="kb-article-toc">
                {structuredContent.sections.map((section, index) => (
                  <button
                    key={section.id}
                    type="button"
                    className="kb-article-toc-link"
                    onClick={() => scrollToSection(section.id)}
                  >
                    <span className="kb-article-toc-number">{String(index + 1).padStart(2, '0')}</span>
                    <span className="kb-article-toc-text">{section.title}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          <Card padding="lg" className="kb-side-panel">
            <div className="kb-side-panel-title">Сводка</div>
            <div className="kb-article-overview-grid">
              {overviewItems.map((item) => (
                <div key={item.label} className="kb-article-overview-item">
                  <div className="kb-article-overview-label">{item.label}</div>
                  <div className="kb-article-overview-value">{item.value}</div>
                </div>
              ))}
            </div>
          </Card>

          {relatedArticles.length > 0 && (
            <Card padding="lg" className="kb-side-panel">
              <div className="kb-side-panel-title">Похожие статьи</div>
              <div className="space-y-3">
                {relatedArticles.map((related: Article) => (
                  <Link
                    key={related.id}
                    to={`/knowledge/${related.slug}`}
                    state={{ from: returnTo }}
                    className="group flex items-start gap-3 rounded-xl border border-line bg-fill-2 p-3 transition-colors hover:border-line hover:bg-fill-2"
                  >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-line bg-fill-2 text-[#727b96] group-hover:text-[#a5b4ff]">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#e7eaf3] group-hover:text-fg-primary line-clamp-2">
                        {related.title}
                      </p>
                      <p className="mt-1 text-xs text-[#727b96]">{related.viewCount} просмотров</p>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          <Card padding="lg" className="kb-side-panel">
            <div className="text-center">
              <BookOpen className="mx-auto h-8 w-8 text-[#a5b4ff]" />
              <p className="mt-3 text-sm font-semibold text-[#e7eaf3]">Нужна помощь поддержки?</p>
              <p className="mt-1 text-xs text-[#9aa3bd]">
                Если в статье не хватает ответа, создай обращение и приложи детали проблемы.
              </p>
              <Link to="/tickets/new" className="mt-4 inline-block">
                <Button variant="primary" size="sm">
                  Создать обращение
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>

      {hasDemo && (
        <DemoPlayer
          steps={demoSteps}
          open={demoOpen}
          onClose={() => setDemoOpen(false)}
        />
      )}
    </div>
  );
};

export default ArticlePage;
